/* ===========================================================================
 * home-hero.js — companion to sections/section-home-hero.liquid
 *
 * Two jobs:
 *   1. Hydrate the hero video. Its <source> tags ship with `data-src` rather
 *      than `src`, so nothing downloads until the hero is actually near the
 *      viewport — and nothing downloads at all under prefers-reduced-motion,
 *      where the poster frame is the intended end state.
 *   2. Run the entrance reveal.
 *
 * Fail-open by design: the CSS only hides anything once this script adds
 * `.home-hero--ready`. If the script never runs, the hero renders fully
 * visible with a static poster — the worst case is "no animation", never
 * "invisible headline".
 *
 * Safe to load more than once (theme editor re-renders sections); each hero
 * element is marked as initialised.
 * ======================================================================== */
(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* Seconds into the clip where the product has slid in and settled. Must stay
   * in step with assets/home-hero-poster.jpg, which is that exact frame. */
  var START_AT = 1.2;

  /* Start the video once it is worth paying for. Autoplay can be refused
   * (low power mode, iOS data saver); if it is, the poster stays up, which is
   * a perfectly good hero, so the rejection is swallowed deliberately. */
  function hydrateVideo(video) {
    if (!video || video.dataset.hhHydrated === 'true') return;
    video.dataset.hhHydrated = 'true';

    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    for (var i = 0; i < sources.length; i++) {
      sources[i].setAttribute('src', sources[i].getAttribute('data-src'));
    }

    /* The clip is built to loop: it starts and ends on an empty frame so the
     * product can slide in and out without a seam. That makes frame 0 a blank
     * panel, which is useless as a poster — so the poster is the frame at
     * START_AT instead, and the first play seeks there to match it. Without
     * the seek you'd see the product, then an empty box, then the product
     * sliding back in. Every later loop runs from 0 as intended.
     *
     * Seeking on `loadedmetadata` alone is a race. With preload="none" the
     * element can report metadata before any of the clip is seekable, and a
     * currentTime assignment made then is dropped silently — stranding the
     * first play on the blank frame the poster exists to hide. So gate the
     * seek on the range actually covering START_AT and re-check as buffering
     * progresses.
     *
     * Bounded and self-detaching in every direction: it gives up after
     * MAX_SEEK_TRIES, stops for good if the clip turns out to be shorter than
     * START_AT, and detaches on the first success so later loops still start
     * from 0. Nothing here can re-arm itself. */
    var SEEK_EVENTS = ['loadedmetadata', 'loadeddata', 'canplay', 'progress'];
    var MAX_SEEK_TRIES = 20;
    var seekTries = 0;

    function stopSeeking() {
      for (var e = 0; e < SEEK_EVENTS.length; e++) {
        video.removeEventListener(SEEK_EVENTS[e], trySeek);
      }
    }

    function trySeek() {
      if (seekTries++ >= MAX_SEEK_TRIES) return stopSeeking();

      // NaN until metadata lands — wait for a later event rather than giving up.
      var duration = video.duration;
      if (!duration) return;
      if (duration <= START_AT) return stopSeeking();

      var ranges = video.seekable;
      if (!ranges || !ranges.length) return;
      if (ranges.end(ranges.length - 1) < START_AT) return;

      try {
        video.currentTime = START_AT;
      } catch (err) {}
      stopSeeking();
    }

    for (var e = 0; e < SEEK_EVENTS.length; e++) {
      video.addEventListener(SEEK_EVENTS[e], trySeek);
    }

    video.load();
    play(video);
  }

  function play(video) {
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  /* Pause off-screen so a looping video doesn't burn battery down the page. */
  function watchPlayback(video) {
    if (!('IntersectionObserver' in window)) return;

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.1 }
    ).observe(video);
  }

  function init(hero) {
    if (!hero || hero.dataset.hhInit === 'true') return;
    hero.dataset.hhInit = 'true';

    var video = hero.querySelector('[data-hh-video]');

    /* A hidden tab — "open in new tab", a prerender — is served no animation
     * frames at all, so an rAF-driven reveal would never run and the hero
     * would sit invisible until the tab was focused. Skip the animation
     * entirely in that case and leave the hero in its plain visible state. */
    var canAnimate = !REDUCED && document.visibilityState !== 'hidden';

    /* The elements are already hidden — layout/theme.liquid armed them before
     * the first paint so they never flash in and back out. Tell it whether this
     * script is going to finish the job, or whether it should show them now. */
    if (window.themeReveal) window.themeReveal[canAnimate ? 'take' : 'drop']('hh');

    if (canAnimate) {
      // The reveal marker; the hiding already happened in CSS.
      hero.classList.add('home-hero--ready');

      // Next frame, so the browser registers the "before" state and transitions
      // rather than jumping straight to the end.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          hero.classList.add('home-hero--in');
        });
      });

      /* Safety net. If frames stop arriving between the two calls above the
       * reveal would strand the hero at opacity 0, so guarantee the end state
       * on a timer as well — setTimeout still fires when rAF does not. */
      setTimeout(function () {
        hero.classList.add('home-hero--in');
      }, 800);
    }

    if (!video || REDUCED) return;

    if (!('IntersectionObserver' in window)) {
      hydrateVideo(video);
      return;
    }

    // rootMargin gives the clip a head start so it is playing, not buffering,
    // by the time it scrolls in. The hero is usually already on screen anyway.
    var loader = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          hydrateVideo(video);
          watchPlayback(video);
          obs.disconnect();
        });
      },
      { rootMargin: '200px 0px' }
    );

    loader.observe(hero);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-home-hero]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: a re-rendered section arrives with no listeners attached.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
